import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQuery} from '../base-query'
import type {
  ClockDeploymentResponse,
  RonosDevicesResponse,
  UpdateStageRequest,
  AddDeviceRequest,
  UpdateDeviceRequest,
  RemoveDeviceRequest,
  StageKey,
} from '../../components/platform/company/tabs/clock-deployment/types'

/**
 * Wire shape for GET /api/clock-deployment/:siteId
 *
 * The BE returns `ClockDeploymentResponse` directly as the JSON body (no
 * `{ data, error }` envelope), exactly like purchase-order.controller.ts:
 *   return this.clockDeploymentService.getDeployment(siteId, currentUser);
 * RTK Query's own `{ data, error, isLoading }` shape is the "envelope" the FE
 * code sees — the wire payload IS `ClockDeploymentResponse`.
 *
 * Example wire payload:
 * {
 *   "id": "66f1b2c3...",
 *   "isVirtual": false,
 *   "siteId": "site-abc",
 *   "companyId": "cmp-xyz",
 *   "siteTimeZone": "America/Los_Angeles",
 *   "stages": [ { "stage": "ronos_profile_created", "status": "completed", ... }, ... ],
 *   "devices": [ { "id": "dev-1", "name": "iPad #1", "type": "ipad", "status": "active", ... } ],
 *   "activeDeviceCount": 2,
 *   "removedDeviceCount": 0
 * }
 */

// Tag identifiers
const TAG_DEPLOYMENT = 'ClockDeployment'
const TAG_RONOS = 'RonosDevices'

export const clockDeploymentApi = createApi({
  reducerPath: 'ClockDeploymentApi',
  tagTypes: [TAG_DEPLOYMENT, TAG_RONOS],
  baseQuery,
  endpoints: (builder) => ({
    // ── GET deployment ────────────────────────────────────────────────────────
    getDeployment: builder.query<ClockDeploymentResponse, string>({
      providesTags: (_result, _err, siteId) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: (siteId) => ({
        url: `/api/clock-deployment/${siteId}`,
        method: 'GET',
      }),
    }),

    // ── Stage: complete or revert ─────────────────────────────────────────────
    updateStage: builder.mutation<
      ClockDeploymentResponse,
      {siteId: string; stage: StageKey; body: UpdateStageRequest}
    >({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, stage, body}) => ({
        url: `/api/clock-deployment/${siteId}/stages/${stage}`,
        method: 'PUT',
        body,
      }),
    }),

    // ── Device: add ───────────────────────────────────────────────────────────
    addDevice: builder.mutation<ClockDeploymentResponse, {siteId: string; body: AddDeviceRequest}>({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, body}) => ({
        url: `/api/clock-deployment/${siteId}/devices`,
        method: 'POST',
        body,
      }),
    }),

    // ── Device: update metadata ───────────────────────────────────────────────
    updateDevice: builder.mutation<
      ClockDeploymentResponse,
      {siteId: string; deviceId: string; body: UpdateDeviceRequest}
    >({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, deviceId, body}) => ({
        url: `/api/clock-deployment/${siteId}/devices/${deviceId}`,
        method: 'PUT',
        body,
      }),
    }),

    // ── Device: archive (delete entry added by mistake) ───────────────────────
    archiveDevice: builder.mutation<ClockDeploymentResponse, {siteId: string; deviceId: string}>({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, deviceId}) => ({
        url: `/api/clock-deployment/${siteId}/devices/${deviceId}`,
        method: 'DELETE',
      }),
    }),

    // ── Device: remove from site (with reason) ────────────────────────────────
    removeDevice: builder.mutation<
      ClockDeploymentResponse,
      {siteId: string; deviceId: string; body: RemoveDeviceRequest}
    >({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, deviceId, body}) => ({
        url: `/api/clock-deployment/${siteId}/devices/${deviceId}/remove`,
        method: 'PUT',
        body,
      }),
    }),

    // ── Device: restore to site ───────────────────────────────────────────────
    restoreDevice: builder.mutation<ClockDeploymentResponse, {siteId: string; deviceId: string}>({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, deviceId}) => ({
        url: `/api/clock-deployment/${siteId}/devices/${deviceId}/restore`,
        method: 'PUT',
      }),
    }),

    // ── Remove all clocks from site ───────────────────────────────────────────
    removeAllClocks: builder.mutation<
      ClockDeploymentResponse,
      {siteId: string; body: RemoveDeviceRequest}
    >({
      invalidatesTags: (_result, _err, {siteId}) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: ({siteId, body}) => ({
        url: `/api/clock-deployment/${siteId}/remove-all`,
        method: 'POST',
        body,
      }),
    }),

    // ── Undo site-level removal ───────────────────────────────────────────────
    undoRemoval: builder.mutation<ClockDeploymentResponse, string>({
      invalidatesTags: (_result, _err, siteId) => [{type: TAG_DEPLOYMENT, id: siteId}],
      query: (siteId) => ({
        url: `/api/clock-deployment/${siteId}/undo-removal`,
        method: 'PUT',
      }),
    }),

    // ── Ronos devices (separate tag — can be refreshed independently) ─────────
    getRonosDevices: builder.query<RonosDevicesResponse, string>({
      providesTags: (_result, _err, siteId) => [{type: TAG_RONOS, id: siteId}],
      query: (siteId) => ({
        url: `/api/clock-deployment/${siteId}/ronos-devices`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetDeploymentQuery,
  useUpdateStageMutation,
  useAddDeviceMutation,
  useUpdateDeviceMutation,
  useArchiveDeviceMutation,
  useRemoveDeviceMutation,
  useRestoreDeviceMutation,
  useRemoveAllClocksMutation,
  useUndoRemovalMutation,
  useGetRonosDevicesQuery,
} = clockDeploymentApi
